import { useCallback, useEffect, useState } from 'react';

const TTL_HOURS = 24;
const TTL_MS = TTL_HOURS * 60 * 60 * 1000;

/**
 * Custom hook for persisting booking form data to localStorage with 24-hour TTL
 * @param {string} quoteUid - Unique quote identifier
 * @returns {Object} Persistence functions and state
 */
const useLocalStoragePersistence = (quoteUid) => {
  const [isExpired, setIsExpired] = useState(false);
  const [hasStoredData, setHasStoredData] = useState(false);

  const storageKey = quoteUid ? `afnu_booking_${quoteUid}` : null;

  /**
   * Check if stored data has expired
   * @param {number} timestamp - When the data was saved
   * @returns {boolean}
   */
  const checkExpired = useCallback((timestamp) => {
    if (!timestamp) return true;
    const now = Date.now();
    return now - timestamp > TTL_MS;
  }, []);

  /**
   * Save form data to localStorage
   * @param {Object} data - Data to save
   * @param {Object} data.formValues - Form field values
   * @param {Array} data.baggageState - Baggage selections
   * @param {Object} data.wizardState - Wizard state (currentStep, completedSteps)
   */
  const saveFormData = useCallback((data) => {
    if (!storageKey) return;

    try {
      const payload = {
        timestamp: Date.now(),
        formValues: data.formValues || {},
        baggageState: data.baggageState || [],
        wizardState: data.wizardState || { currentStep: 1, completedSteps: [] },
        paymentsCount: data.paymentsCount || 1
      };

      localStorage.setItem(storageKey, JSON.stringify(payload));
      setHasStoredData(true);
    } catch (error) {
      console.warn('Failed to save booking data to localStorage:', error);
    }
  }, [storageKey]);

  /**
   * Load form data from localStorage
   * @returns {Object|null} Stored data or null if expired/not found
   */
  const loadFormData = useCallback(() => {
    if (!storageKey) return null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        setHasStoredData(false);
        return null;
      }

      const data = JSON.parse(stored);

      // Check TTL
      if (checkExpired(data.timestamp)) {
        // Data has expired, clear it
        localStorage.removeItem(storageKey);
        setIsExpired(true);
        setHasStoredData(false);
        return null;
      }

      setHasStoredData(true);
      setIsExpired(false);
      return data;
    } catch (error) {
      console.warn('Failed to load booking data from localStorage:', error);
      return null;
    }
  }, [storageKey, checkExpired]);

  /**
   * Clear stored form data
   */
  const clearFormData = useCallback(() => {
    if (!storageKey) return;

    try {
      localStorage.removeItem(storageKey);
      setHasStoredData(false);
      setIsExpired(false);
    } catch (error) {
      console.warn('Failed to clear booking data from localStorage:', error);
    }
  }, [storageKey]);

  /**
   * Update only specific parts of stored data
   * @param {Object} updates - Partial data to update
   */
  const updateFormData = useCallback((updates) => {
    if (!storageKey) return;

    try {
      const existing = loadFormData();
      const merged = {
        timestamp: Date.now(),
        formValues: { ...(existing?.formValues || {}), ...(updates.formValues || {}) },
        baggageState: updates.baggageState !== undefined ? updates.baggageState : (existing?.baggageState || []),
        wizardState: updates.wizardState !== undefined ? updates.wizardState : (existing?.wizardState || { currentStep: 1, completedSteps: [] })
      };

      localStorage.setItem(storageKey, JSON.stringify(merged));
      setHasStoredData(true);
    } catch (error) {
      console.warn('Failed to update booking data in localStorage:', error);
    }
  }, [storageKey, loadFormData]);

  /**
   * Get time remaining before expiration
   * @returns {Object|null} { hours, minutes } or null if no data
   */
  const getTimeRemaining = useCallback(() => {
    if (!storageKey) return null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const data = JSON.parse(stored);
      if (!data.timestamp) return null;

      const elapsed = Date.now() - data.timestamp;
      const remaining = TTL_MS - elapsed;

      if (remaining <= 0) return null;

      const hours = Math.floor(remaining / (60 * 60 * 1000));
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

      return { hours, minutes };
    } catch (error) {
      return null;
    }
  }, [storageKey]);

  // Check for stored data on mount
  useEffect(() => {
    if (storageKey) {
      const data = loadFormData();
      setHasStoredData(!!data);
    }
  }, [storageKey, loadFormData]);

  return {
    // Functions
    saveFormData,
    loadFormData,
    clearFormData,
    updateFormData,
    getTimeRemaining,

    // State
    isExpired,
    hasStoredData,
    storageKey
  };
};

export default useLocalStoragePersistence;
